import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import SyncWithQuickbooksModal from 'components/Invoice/SyncWithQuickbooksModal';
import ProductServiceSyncWithQuickbooksModal from 'components/Invoice/ProductServiceSyncWithQuickbooksModal';

const reason = (error, fallback) => {
  const data = error && error.response && error.response.data;
  const value = data && (data.errors || data.error);
  return value ? [].concat(value).join(' ') : fallback;
};

// Pushing an invoice to QuickBooks (moved from the old invoice page, same
// steps): link any products QuickBooks doesn't know yet, link the client to a
// QuickBooks customer if it isn't, then confirm and sync (or update an invoice
// synced before). `before`
// runs first (the editor saves unsaved changes, since QuickBooks gets the
// saved invoice, and unlinking reloads it). The editor gets push() and
// unlink() through `register`.
@inject('invoiceStore', 'quickbooksStore') @observer
class QuickbooksSync extends React.Component {
  @observable linkClientOpen = false;
  @observable linkProductsOpen = false;
  @observable confirmSync = false;
  @observable confirmUpdate = false;

  componentDidMount() {
    this.props.quickbooksStore.loadQuickbooksItems();
    if (this.props.register) this.props.register({ push: this.push, unlink: this.unlink });
  }

  get invoice() {
    return this.props.invoiceStore.invoice.object;
  }

  push = () => {
    const { quickbooksStore, before } = this.props;
    return (before ? before() : Promise.resolve())
      .then(() => quickbooksStore.checkProductServicesStatus(this.invoice))
      .then((response) => {
        if (response.data.product_services_link_required) {
          this.linkProductsOpen = true;
          return;
        }
        const client = this.invoice.attributes.client || {};
        if (client.synced_with_quickbooks) {
          if (this.invoice.attributes.synced_with_quickbooks) this.confirmUpdate = true;
          else this.confirmSync = true;
        } else {
          this.linkClientOpen = true;
          quickbooksStore.loadQuickbooksCustomers();
        }
      })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't check the invoice with QuickBooks. Try again."), intent: Intent.DANGER }); });
  };

  unlink = () => {
    const { quickbooksStore, invoiceStore } = this.props;
    const { before } = this.props;
    const client = this.invoice.attributes.client || {};
    if (!client.synced_with_quickbooks) return Promise.resolve();
    // Unlinking reloads the invoice: unsaved changes are saved first.
    return (before ? before() : Promise.resolve())
      .then(() => quickbooksStore.unlinkClientFromQuickbooks(client))
      .then(() => {
        invoiceStore.getInvoice({ uuid: this.invoice.attributes.id });
        Toast.show({ message: `${client.name || 'The client'} unlinked from QuickBooks.`, intent: Intent.SUCCESS });
      })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't unlink the client. Try again."), intent: Intent.DANGER }); });
  };

  sync = () => {
    this.confirmSync = false;
    this.props.quickbooksStore.syncInvoiceWithQuickbooks(this.invoice)
      .then(() => { Toast.show({ message: 'Invoice synced to QuickBooks.', intent: Intent.SUCCESS }); })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't sync the invoice. Try again."), intent: Intent.DANGER }); });
  };

  update = () => {
    this.confirmUpdate = false;
    this.props.quickbooksStore.updateInvoiceWithQuickbooks(this.invoice)
      .then(() => { Toast.show({ message: 'Invoice updated in QuickBooks.', intent: Intent.SUCCESS }); })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't update the invoice in QuickBooks. Try again."), intent: Intent.DANGER }); });
  };

  render() {
    return (
      <>
        <SyncWithQuickbooksModal
          isOpen={this.linkClientOpen}
          onClose={() => { this.linkClientOpen = false; }}
          onSuccess={() => { this.linkClientOpen = false; this.confirmSync = true; }}
        />
        <ProductServiceSyncWithQuickbooksModal
          isOpen={this.linkProductsOpen}
          onClose={() => { this.linkProductsOpen = false; }}
          onSuccess={() => { this.linkProductsOpen = false; this.push(); }}
        />
        <Alert
          isOpen={this.confirmSync}
          icon="cloud-upload"
          intent={Intent.PRIMARY}
          cancelButtonText="Cancel"
          confirmButtonText="Sync to QuickBooks"
          onCancel={() => { this.confirmSync = false; }}
          onConfirm={this.sync}
        >
          <strong>Sync this invoice to QuickBooks?</strong>
          <p>It's added to QuickBooks for {(this.invoice && this.invoice.attributes.client && this.invoice.attributes.client.name) || 'the client'}.</p>
        </Alert>
        <Alert
          isOpen={this.confirmUpdate}
          icon="cloud-upload"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Update in QuickBooks"
          onCancel={() => { this.confirmUpdate = false; }}
          onConfirm={this.update}
        >
          <strong>Update this invoice in QuickBooks?</strong>
          <p>What was synced before is replaced by the invoice as it is now, and can't be brought back.</p>
        </Alert>
      </>
    );
  }
}

export default QuickbooksSync;
